import Typography from "@mui/material/Typography";

import { LoadingOverlay } from "@covertree/ui";

import {
  saveExternalPolicyDocuments,
  selectExternalPolicyDocuments,
  selectExtractionDocument,
  selectProofOfCoverageDetails,
} from "@redux/coverages/coveragesSlice";
import {
  useAddCovertreeCoverageMutation,
  useAddExternalCoverageMutation,
} from "@redux/coverages/coveragesApi";
import { useAppSelector } from "@redux/hooks/useAppSelector";
import {
  addedCoverageRedirectPath,
  isCoverageDocument,
  moveBackInPathname,
} from "@utils";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import ProofOfCoverageDetailsContent from "./ProofOfCoverageDetailsContent";
import { PolicyType, Resident } from "@graphql/api";
import { useUploadCoverageDocumentsMutation } from "@redux/documents/documentsRestApi";
import { useState } from "react";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { PROOF_OF_COVERAGE_ROUTES } from "@routing/Residents/ProofOfCoverage";

export default function ProofOfCoverageDetails() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { residentId } = useParams();

  const coverage = useAppSelector(selectProofOfCoverageDetails);
  const files = useAppSelector(selectExternalPolicyDocuments);
  const extractionDocument = useAppSelector(selectExtractionDocument);
  // The document from the Policy Document step is attached by id, unless it was removed in the Documents step.
  const isExtractionFile = (file: File) =>
    !!extractionDocument && file.name === extractionDocument.fileName;
  const documentIds = files.some(isExtractionFile)
    ? [extractionDocument.id]
    : [];

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);

  const [
    addCovertreeCoverage,
    {
      data: ctCoverage,
      isSuccess: isCtCoverageSuccess,
      isLoading: isCtCoverageLoading,
      isError: isCtCoverageError,
    },
  ] = useAddCovertreeCoverageMutation();

  const handleAddCtCoverage = () => {
    addCovertreeCoverage({
      policyLocator: coverage.policyId,
      residentId: residentId,
    });
  };

  const [
    addExternalCoverageData,
    {
      data: externalCoverage,
      isLoading: isExternalCoverageLoading,
      isError: isExternalCoverageError,
      isSuccess: isExternalCoverageSuccess,
    },
  ] = useAddExternalCoverageMutation();

  const [uploadCoverageDocuments] = useUploadCoverageDocumentsMutation();

  const uploadDocuments = async (resident: Resident) => {
    const isAttached = resident.coverages[0].documents?.some(
      ({ id }) => id === extractionDocument?.id
    );
    // If attaching by id failed, the file is uploaded the usual way instead.
    const filesToUpload = isAttached
      ? files.filter((file) => !isExtractionFile(file))
      : files;

    const results = await Promise.all(
      filesToUpload.map((file) =>
        uploadCoverageDocuments({
          residentId: resident.id,
          coverageId: resident.coverages[0].id,
          file,
        })
      )
    );

    const failedFiles = filesToUpload.filter((file) => {
      const result = results.find((res) => res?.["data"]?.name === file.name);
      return !isCoverageDocument(result?.["data"]);
    });

    if (!!failedFiles.length) {
      dispatch(
        saveExternalPolicyDocuments({
          files: failedFiles,
          isFailed: true,
          coverageId: resident.coverages[0].id,
        })
      );
    } else {
      setIsUploadSuccess(true);
    }
  };

  const handleAddExternalCoverage = async () => {
    setIsUploadLoading(true);

    const {
      agencyName,
      agencyAddress,
      agencyPhoneNumber,
      agencyEmail,
      annualCost,
      coverageDetails,
      insuranceCarrierName,
      isInternalSale,
      policyId,
      policyDescription,
      policyEffectiveDate,
      policyEndDate,
    } = coverage;

    const response = await addExternalCoverageData({
      policy: {
        agencyName,
        agencyAddress,
        agencyPhoneNumber,
        agencyEmail,
        annualCost,
        coverageDetails: coverageDetails
          ? {
              ...coverageDetails,
              occupancy: coverageDetails.occupancy,
              personalLiabilityLimit: coverageDetails.personalLiabilityLimit,
            }
          : null,
        insuranceCarrierName,
        isInternalSale,
        policyId,
        policyDescription,
        policyEffectiveDate,
        policyEndDate,
      },
      residentId: residentId,
      documentIds,
    }).unwrap();

    if (response.resident) {
      await uploadDocuments(response.resident);
    }
    setIsUploadLoading(false);
  };

  const handleSubmit = async () => {
    if (coverage.policyType === PolicyType.HomeOwners) {
      handleAddCtCoverage();
    } else {
      await handleAddExternalCoverage();
    }
  };

  const isLoading =
    isCtCoverageLoading || isExternalCoverageLoading || isUploadLoading;

  if (isLoading) return <LoadingOverlay />;

  if (isCtCoverageError || isExternalCoverageError)
    return (
      <Typography>
        Could not{" "}
        {coverage.policyType === PolicyType.HomeOwners
          ? "get any data for CT policy ID"
          : "add external policy"}
        . Please check your internet connection or contact our team.
      </Typography>
    );

  if (isCtCoverageSuccess) {
    navigate(
      addedCoverageRedirectPath(searchParams, location.pathname, ctCoverage),
      {
        replace: true,
      }
    );
    return <LoadingOverlay />;
  }

  if (isExternalCoverageSuccess && !isUploadLoading) {
    if (isUploadSuccess) {
      navigate(
        addedCoverageRedirectPath(
          searchParams,
          location.pathname,
          externalCoverage
        ),
        {
          replace: true,
        }
      );
    } else {
      navigate(
        {
          pathname: `${moveBackInPathname(location.pathname, 2)}/${PROOF_OF_COVERAGE_ROUTES.COVERAGE_DOCUMENTS}`,
          search: createSearchParams(searchParams).toString(),
        },
        { replace: true }
      );
    }
    return <LoadingOverlay />;
  }

  if (!coverage) {
    return (
      <>
        <Typography lineHeight="400%">
          Coverage not found. Contact us or try again later.
        </Typography>
      </>
    );
  }
  return (
    <ProofOfCoverageDetailsContent
      coverage={coverage}
      onSubmit={handleSubmit}
    />
  );
}
